import { format as formatDateFns, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const LOCALE = 'de-DE';
const PLACEHOLDER = '–';

/**
 * Normalises numbers coming from the API. The backend delivers money as raw
 * decimal strings or numbers ("1234.50"); we never compute with them.
 * @param {number|string|null|undefined} value
 * @returns {number|null}
 */
export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null}
 */
export function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

/**
 * Formats an amount as EUR in de-DE, e.g. "1.234,50 €".
 * @param {number|string|null|undefined} value
 * @param {{ currency?: string, sign?: boolean, fractionDigits?: number, fallback?: string }} [options]
 *   sign: always show a sign ("+1.234,50 €" / "-1.234,50 €"; zero has no sign)
 */
export function formatMoney(value, options = {}) {
  const { currency = 'EUR', sign = false, fractionDigits = 2, fallback = PLACEHOLDER } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: sign ? 'exceptZero' : 'auto',
  }).format(n);
}

/**
 * Plain number, de-DE grouping, e.g. "12.345,6".
 * @param {number|string|null|undefined} value
 * @param {{ minimumFractionDigits?: number, maximumFractionDigits?: number, fallback?: string }} [options]
 */
export function formatNumber(value, options = {}) {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2, fallback = PLACEHOLDER } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return new Intl.NumberFormat(LOCALE, { minimumFractionDigits, maximumFractionDigits }).format(n);
}

/**
 * Percentage. By default the input is a percentage value (5.25 → "5,25 %").
 * Pass `{ ratio: true }` if the value is a ratio (0.0525 → "5,25 %").
 * @param {number|string|null|undefined} value
 * @param {{ ratio?: boolean, fractionDigits?: number, sign?: boolean, fallback?: string }} [options]
 */
export function formatPercent(value, options = {}) {
  const { ratio = false, fractionDigits = 2, sign = false, fallback = PLACEHOLDER } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: sign ? 'exceptZero' : 'auto',
  }).format(ratio ? n : n / 100);
}

/**
 * "dd.MM.yyyy"
 * @param {Date|string|number|null|undefined} value
 * @param {{ fallback?: string }} [options]
 */
export function formatDate(value, options = {}) {
  const d = toDate(value);
  return d ? formatDateFns(d, 'dd.MM.yyyy', { locale: de }) : (options.fallback ?? PLACEHOLDER);
}

/**
 * "dd.MM.yyyy HH:mm"
 * @param {Date|string|number|null|undefined} value
 * @param {{ fallback?: string }} [options]
 */
export function formatDateTime(value, options = {}) {
  const d = toDate(value);
  return d ? formatDateFns(d, 'dd.MM.yyyy HH:mm', { locale: de }) : (options.fallback ?? PLACEHOLDER);
}

/**
 * Month axis label, "MMM yyyy" with German locale, e.g. "Jan. 2026".
 * @param {Date|string|number|null|undefined} value
 * @param {{ fallback?: string }} [options]
 */
export function formatMonth(value, options = {}) {
  const d = toDate(value);
  return d ? formatDateFns(d, 'MMM yyyy', { locale: de }) : (options.fallback ?? PLACEHOLDER);
}

/**
 * Area in m², e.g. "72,5 m²".
 * @param {number|string|null|undefined} value
 * @param {{ fallback?: string }} [options]
 */
export function formatArea(value, options = {}) {
  const n = toNumber(value);
  if (n === null) return options.fallback ?? PLACEHOLDER;
  return `${formatNumber(n)}\u00a0m²`;
}

/**
 * File size in KB/MB (de-DE), e.g. "1,2 MB". Bytes come from the API (`documents.size`).
 * @param {number|string|null|undefined} bytes
 * @param {{ fallback?: string }} [options]
 */
export function formatFileSize(bytes, options = {}) {
  const n = toNumber(bytes);
  if (n === null) return options.fallback ?? PLACEHOLDER;
  if (n < 1024) return `${formatNumber(n)}\u00a0B`;
  if (n < 1024 * 1024) return `${formatNumber(n / 1024, { maximumFractionDigits: 0 })}\u00a0KB`;
  return `${formatNumber(n / (1024 * 1024), { maximumFractionDigits: 1 })}\u00a0MB`;
}

/**
 * ISO timestamp → value for `<input type="datetime-local">` (local time, minutes), '' if empty.
 * @param {string|null|undefined} value
 */
export function toDateTimeLocal(value) {
  const d = toDate(value);
  return d ? formatDateFns(d, "yyyy-MM-dd'T'HH:mm") : '';
}
